export const DEFAULT_QUIZ_RULES_HTML = `<h2>Quiz Rules</h2>
<p>Please read the following instructions carefully before starting:</p>
<ol>
  <li><strong>No Cheating</strong><br/>Any form of cheating is strictly prohibited, including capturing content using phones, screenshots, or other devices.</li>
  <li><strong>No External Assistance</strong><br/>You must complete the quiz independently without using notes, resources, or help from others.</li>
  <li><strong>Academic Integrity</strong><br/>All participants are expected to follow academic integrity guidelines at all times.</li>
  <li><strong>Time Limit</strong><br/>Each quiz must be completed within the allocated time. The timer runs continuously and will not pause, even if you leave the app or exit the quiz.</li>
  <li><strong>Auto-Submission</strong><br/>The quiz will be automatically submitted once the time expires.</li>
  <li><strong>No Backtracking</strong><br/>Once a question is answered, you cannot return to it.</li>
  <li><strong>Single Attempt</strong><br/>Each quiz can only be taken once unless specified otherwise.</li>
  <li><strong>Full Attention Required</strong><br/>Leaving the quiz screen may affect your progress or lead to automatic submission.</li>
  <li><strong>Stable Connection</strong><br/>Ensure you have a reliable internet connection before starting the quiz.</li>
  <li><strong>Question Format</strong><br/>Questions may include text, images, histology slides, or 3D models, and are primarily multiple-choice.</li>
</ol>
<p><em>By starting this quiz, you confirm that you understand and agree to these rules.</em></p>`;

export function emptyQuizDraft() {
  return {
    title: "",
    time_limit_minutes: 30,
    shuffle_questions: true,
    shuffle_answers: true,
    show_score_immediately: true,
    show_answers_after: true,
    rules_html: DEFAULT_QUIZ_RULES_HTML,
    topic_rules: [],
    bank_preference: null,
    awaiting_confirmation: false,
    confirmation_fingerprint: null,
    pending_generated_questions: null,
    pending_presentation: null,
  };
}

function hasMarksOrResultsKeywords(t) {
  return /\b(results?|scores?|marks?|grades?|performance|averages?|analytics|analysis|progress|attempts?|performed)\b/.test(
    t
  );
}

function isRosterDirectoryAsk(t) {
  if (hasMarksOrResultsKeywords(t)) return false;
  return (
    /\b(users?|roster|directory|user details?)\b/.test(t) ||
    /\bauthorized scope\b/.test(t) ||
    /\bstudents? (in|of|at) (my |the )?universit/.test(t) ||
    /\busers? (in|of|at) (my |the )?universit/.test(t) ||
    (/\b(list|show|tell|who|details?|about)\b/.test(t) &&
      /\b(students?|professors?|users?)\b/.test(t) &&
      !/\b(classes?)\b/.test(t))
  );
}

/** Heuristic: user wants student quiz results / marks / performance analysis (not create). */
export function isQuizResultsIntent(text) {
  const t = String(text || "").toLowerCase().trim();
  if (!t) return false;
  const createOnly =
    /\bcreate\b/.test(t) &&
    /\bquiz\b/.test(t) &&
    !hasMarksOrResultsKeywords(t);
  if (createOnly) return false;
  // Roster / user-details asks are handled by isQuizUsersIntent.
  if (isRosterDirectoryAsk(t)) return false;
  return (
    hasMarksOrResultsKeywords(t) ||
    /\b(check|show|tell|analyze|analyse|summarize|summarise)\b[\s\S]{0,40}\b(students?|classes?|attempts?)\b/.test(
      t
    ) ||
    /\bstudents?\b[\s\S]{0,40}\b(results?|scores?|marks?|grades?|performed|performance)\b/.test(
      t
    ) ||
    /show the results|check quiz results|^my_results$/.test(t)
  );
}

/**
 * Heuristic: user wants university/class user roster or user details
 * (not marks/results — those use isQuizResultsIntent).
 */
export function isQuizUsersIntent(text) {
  const t = String(text || "").toLowerCase().trim();
  if (!t) return false;
  return isRosterDirectoryAsk(t);
}

/** Short “N questions M min” follow-up during quiz create. */
export function isQuizSizeFollowUp(text) {
  const t = String(text || "").toLowerCase().trim();
  if (!t) return false;
  if (/\bcreate\b/.test(t) && /\bquiz\b/.test(t)) return false;
  const hasCount = /\b\d+\s*(?:medium|easy|hard)?\s*questions?\b/.test(t) || /\b\d+\s*qs?\b/.test(t);
  const hasMinutes = /\b\d+\s*[-–—.]?\s*min(?:ute)?s?\b/.test(t) || /\b\d+\s*m\b/.test(t);
  return hasCount && hasMinutes;
}

/** Small Levenshtein distance for typo-tolerant keyword matching. */
function editDistance(a, b) {
  const s = String(a || "");
  const t = String(b || "");
  if (s === t) return 0;
  if (!s.length) return t.length;
  if (!t.length) return s.length;
  const prev = new Array(t.length + 1);
  const cur = new Array(t.length + 1);
  for (let j = 0; j <= t.length; j += 1) prev[j] = j;
  for (let i = 1; i <= s.length; i += 1) {
    cur[0] = i;
    for (let j = 1; j <= t.length; j += 1) {
      const cost = s[i - 1] === t[j - 1] ? 0 : 1;
      cur[j] = Math.min(cur[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
    }
    for (let j = 0; j <= t.length; j += 1) prev[j] = cur[j];
  }
  return prev[t.length];
}

/**
 * True when a token is “presentation” or a close typo
 * (oresenation, presnntation, presention, …).
 */
export function looksLikePresentationWord(token) {
  const w = String(token || "")
    .toLowerCase()
    .replace(/[^a-z]/g, "");
  if (!w) return false;
  if (/^presenta?t?ions?$/.test(w)) return true;
  const known = new Set([
    "presentation",
    "presentations",
    "presention",
    "presentions",
    "presantation",
    "presantations",
    "presenation",
    "presenations",
    "presnetaion",
    "presntation",
    "presnntation",
    "oresenation",
    "oresenations",
    "prsentation",
    "presentaiton",
    "presentaion",
    "presnetation",
    "prasentation",
    "persentation",
    "presnetion",
  ]);
  if (known.has(w)) return true;
  if (w.length < 8 || w.length > 16) return false;
  // Must vaguely look like presentation (pre… / or… + …tion).
  if (!/^(pres|pre|prs|ore|per|pra)/.test(w) || !/t?ions?$/.test(w)) return false;
  return (
    editDistance(w, "presentation") <= 3 ||
    editDistance(w, "presentations") <= 3 ||
    editDistance(w.replace(/s$/, ""), "presentation") <= 3
  );
}

/** Presentation create / edit / confirm intents (kept separate from quiz). */
export function isPresentationIntent(text) {
  const raw = String(text || "");
  const t = raw.toLowerCase();
  if (!t.trim()) return false;
  if (
    /^(propose_presentation|confirm_apply_presentation|discard_presentation|presentation_confirm|presentation_discard|presentation_remove_slide:|presentation_update_slide:|presentation_remove_3d:|presentation_library_search:|presentation_add_media:)/.test(
      t.trim()
    )
  ) {
    return true;
  }
  if (
    /\b(make|create|build|move)\b/.test(t) &&
    /\b(3d|model|viewer|title)\b/.test(t) &&
    /\b(bigger|smaller|larger|left|right|up|down)\b/.test(t)
  ) {
    return true;
  }
  // Tokenize so “make. presnntation abotu …” still matches.
  const tokens = t.split(/[^a-z0-9]+/).filter(Boolean);
  if (tokens.some(looksLikePresentationWord)) return true;
  return /\bslides?\b/.test(t) && /\b(make|create|creat|build|put|add)\b/.test(t);
}

/** Heuristic: message looks like quiz-copilot intent (quiz only — not presentations). */
export function isQuizCopilotIntent(text) {
  const t = String(text || "").toLowerCase();
  if (!t.trim()) return false;
  // Presentations have their own lane — never treat them as quiz asks.
  if (isPresentationIntent(t)) return false;
  if (isQuizResultsIntent(t) || isQuizUsersIntent(t)) return true;
  if (isQuizSizeFollowUp(t)) return true;
  if (/\b(delete|remove|erase|destroy|purge)\b/.test(t) && /\b(quiz|question|title)\b/.test(t)) {
    return true; // still route to copilot so it can refuse securely
  }
  return (
    /\bquiz\b/.test(t) ||
    /\bquestion bank\b/.test(t) ||
    /\btime limit\b/.test(t) ||
    /^(cat:|topic:|bank:|start_quiz|generate_missing|confirm_save_generated|discard_generated|open_question_bank|open_copilot|continue_quiz)/.test(
      t.trim()
    ) ||
    /^(create|creat)\s+quiz\b/.test(t.trim()) ||
    /^(create quiz|looks good)\b/.test(t.trim())
  );
}

/** Pull topic from free-typed presentation asks, including common typos. */
export function extractPresentationTopicFromText(text) {
  const raw = String(text || "").trim();
  if (!raw) return "Medical topic";
  const about =
    raw.match(
      /(?:[a-z]*pres[a-z]*t[a-z]*ions?|oresenations?|slides?)\s*[.,]?\s*(?:about|abotu|on|for)\s+(.+)/i
    )?.[1] ||
    raw.match(
      /(?:make|create|creat|build)\s+(?:a\s+)?(?:[a-z]*pres[a-z]*t[a-z]*ions?|oresenations?)\s*[.,]?\s*(?:about|abotu|on|for)?\s*(.+)/i
    )?.[1] ||
    raw
      .replace(
        /^(make|create|creat|build)\s+(a\s+)?([a-z]*pres[a-z]*t[a-z]*ions?|oresenations?)\s*[.,]?\s*/i,
        ""
      )
      .replace(/^(about|abotu|on|for)\s+/i, "")
      .trim();
  return (
    String(about || "")
      .replace(/\s+with\s+\d+\s*slides?/i, "")
      .replace(/\s+and\s+put\b.*$/i, "")
      .replace(/[.?!]+$/, "")
      .trim() || "Medical topic"
  );
}

/** Build a client-side presentation draft when MCP is unreachable (still shows Create button). */
export function buildLocalPresentationPending(text, { slideCount = 5, presentationId = null } = {}) {
  const raw = String(text || "").trim();
  const topic = extractPresentationTopicFromText(raw);
  const countMatch = raw.match(/(\d+)\s*slides?\b/i);
  const count = Math.min(
    12,
    Math.max(3, Number(countMatch?.[1] || slideCount) || 5)
  );
  const templates = [
    {
      title: topic,
      bullets: [
        `Overview of ${topic}`,
        "Why this topic matters clinically",
        "What you will learn in this presentation",
      ],
    },
    {
      title: `Types / structure of ${topic}`,
      bullets: [
        "Main categories or regions",
        "Key identifying features",
        "How to recognize them under the microscope or in the lab",
      ],
    },
    {
      title: `Components of ${topic}`,
      bullets: [
        "Cells involved",
        "Extracellular / supporting elements",
        "Functional relationships",
      ],
    },
    {
      title: `Functions of ${topic}`,
      bullets: [
        "Primary physiological roles",
        "Support and protection",
        "Clinical relevance",
      ],
    },
    {
      title: "Summary & takeaways",
      bullets: [
        `Key points about ${topic}`,
        "Common student confusions",
        "Suggested next study steps",
      ],
    },
  ];
  while (templates.length < count) {
    const n = templates.length;
    templates.push({
      title: `${topic} — part ${n}`,
      bullets: [`Core concept ${n}`, "Structure and function", "Clinical correlation"],
    });
  }
  const slides = templates.slice(0, count).map((s, i) => ({
    id: `pending-${i + 1}`,
    order: i + 1,
    slide_kind: "content",
    title: s.title,
    bullets: s.bullets,
    body: "",
    speaker_notes: "",
    related_3d_hint: i === 0 || i === 1 ? topic : null,
    related_3d: null,
  }));
  return {
    fingerprint: `local-${Date.now().toString(36)}`,
    topic,
    presentation_title: topic,
    slide_count: slides.length,
    slides,
    related_3d_suggestions: [],
    target_presentation_id: presentationId || null,
    route_mode: presentationId ? "apply_to_current" : "create_new",
    created_at: new Date().toISOString(),
    local_only: true,
  };
}

export function draftToQuizForm(draft) {
  const d = draft || emptyQuizDraft();
  return {
    title: d.title || "",
    time_limit_minutes: d.time_limit_minutes ?? 30,
    shuffle_questions: d.shuffle_questions !== false,
    shuffle_answers: d.shuffle_answers !== false,
    show_score_immediately: d.show_score_immediately !== false,
    show_answers_after: d.show_answers_after !== false,
    rules_html: d.rules_html || DEFAULT_QUIZ_RULES_HTML,
    topic_rules:
      Array.isArray(d.topic_rules) && d.topic_rules.length
        ? d.topic_rules.map((r) => ({
            professor_topic_id: r.professor_topic_id || "",
            bank_source: r.bank_source === "microlab" ? "microlab" : "private",
            catalogue: r.catalogue || "",
            quiz_title_ids: Array.isArray(r.quiz_title_ids) ? r.quiz_title_ids : [],
            num_questions: r.num_questions || 1,
            difficulty_filter: r.difficulty_filter || "",
          }))
        : [
            {
              professor_topic_id: "",
              bank_source: "private",
              catalogue: "",
              quiz_title_ids: [],
              num_questions: 2,
              difficulty_filter: "",
            },
          ],
  };
}

export function quizFormToDraft(form, prev = emptyQuizDraft()) {
  return {
    ...prev,
    title: form.title || "",
    time_limit_minutes: form.time_limit_minutes ?? 30,
    shuffle_questions: form.shuffle_questions !== false,
    shuffle_answers: form.shuffle_answers !== false,
    show_score_immediately: form.show_score_immediately !== false,
    show_answers_after: form.show_answers_after !== false,
    rules_html: form.rules_html || DEFAULT_QUIZ_RULES_HTML,
    topic_rules: Array.isArray(form.topic_rules) ? form.topic_rules : [],
  };
}
